import React, { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, IconButton, CircularProgress, Alert, useTheme, alpha } from '@mui/material';
import { CloudUpload as UploadIcon, Delete as DeleteIcon, Image as ImageIcon } from '@mui/icons-material';
import { useImageUpload } from '@modules/offers';
import { env } from '@core/config/env';
import { isValidFileSize, isValidImageType } from '@modules/offers/utils';

interface ImageUploadProps {
  value?: string;
  label?: string;
  onChange: (url: string | null) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  maxSizeMB?: number;
  offerIdForUpload?: number | string;
  uploadFieldName?: string;
  'data-testid'?: string;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  maxSizeMB = 5,
  'data-testid': testId,
  offerIdForUpload,
  uploadFieldName,
}) => {
  const theme = useTheme();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedPreviewUrl, setUploadedPreviewUrl] = useState<string | null>(null);

  const { mutate: uploadImage, isPending: isLoading } = useImageUpload({
    onSuccess: ({ filename, url }) => {
      // store filename in form, but preview using server-provided URL
      onChange(filename);
      setUploadedPreviewUrl(url);
      setUploadError(null);
    },
    onError: (error) => {
      setUploadError(error.message);
    },
  });

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      // Validate file type
      if (!isValidImageType(file)) {
        setUploadError('Please select a valid image file (PNG, JPG, JPEG)');
        return;
      }

      // Validate file size
      if (!isValidFileSize(file, maxSizeMB)) {
        setUploadError(`File size must be less than ${maxSizeMB}MB`);
        return;
      }

      // Upload original file as binary
      uploadImage({ file, offerId: offerIdForUpload, name: uploadFieldName });
    },
    [uploadImage, maxSizeMB, offerIdForUpload, uploadFieldName],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png'],
    },
    multiple: false,
    disabled: disabled || isLoading,
  });

  const handleRemove = () => {
    onChange(null);
    setUploadError(null);
  };

  const displayError = error || uploadError;

  const derivedPreviewUrl = useMemo(() => {
    // If just uploaded, prefer server-returned URL
    if (uploadedPreviewUrl) return uploadedPreviewUrl;
    if (!value) return null;
    // If value is already a full URL, use it
    if (/^https?:\/\//i.test(value)) return value;
    // If editing (offerIdForUpload present) and we have only filename, use the fixed base for display
    // if (offerIdForUpload) {
    return `${env.imageUrl}/${value}`;
    // }
    // On create (no offerId), we don't have a stable URL unless the server returned it (handled above)
    return null;
  }, [uploadedPreviewUrl, value, offerIdForUpload]);

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          mb: 1,
          color: theme.palette.text.primary,
          fontWeight: 500,
        }}
      >
        {required && (
          <Typography component="span" sx={{ color: theme.palette.error.main, ml: 0.5 }}>
            *
          </Typography>
        )}
      </Typography>

      {derivedPreviewUrl ? (
        // Show uploaded image
        <Box
          sx={{
            position: 'relative',
            display: 'inline-block',
            border: `2px solid ${theme.palette.divider}`,
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: theme.palette.background.paper,
          }}
          data-testid={testId}
        >
          <Box
            component="img"
            src={derivedPreviewUrl}
            alt={'IMG'}
            sx={{
              width: 200,
              height: 150,
              objectFit: 'contain',
              display: 'block',
            }}
          />
          <IconButton
            onClick={handleRemove}
            disabled={disabled}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: alpha(theme.palette.error.main, 0.8),
              color: theme.palette.error.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.error.main,
              },
            }}
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        // Show upload area
        <Box
          {...getRootProps()}
          sx={{
            border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
            borderRadius: 2,
            padding: 3,
            textAlign: 'center',
            cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
            backgroundColor: isDragActive ? alpha(theme.palette.primary.main, 0.1) : theme.palette.background.paper,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: disabled || isLoading ? theme.palette.divider : theme.palette.primary.main,
              backgroundColor:
                disabled || isLoading ? theme.palette.background.paper : alpha(theme.palette.primary.main, 0.05),
            },
          }}
          data-testid={testId}
        >
          <input {...getInputProps()} />

          {isLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary">
                Uploading image...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              {isDragActive ? (
                <UploadIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />
              ) : (
                <ImageIcon sx={{ fontSize: 48, color: theme.palette.text.secondary }} />
              )}
              <Box>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  {isDragActive ? 'Drop the image here' : 'Click to upload or drag and drop'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  PNG, JPG, JPEG, up to {maxSizeMB}MB
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {displayError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {displayError}
        </Alert>
      )}
    </Box>
  );
};
